import React, { useCallback, useMemo, useRef } from 'react';

import { useIntl } from 'react-intl';
import { di } from 'react-magnetic-di';

import { useFlags } from '@atlaskit/flag/use-flags';
import StatusErrorIcon from '@atlaskit/icon/core/status-error';
import { Box } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { ExperienceName } from '@atlassian/conversation-assistant-instrumentation/constants/experience-tracker';
import { useAnalytics } from '@atlassian/conversation-assistant-instrumentation/controllers/analytics';
import {
	ASSISTANCE_SERVICE_ERROR_CODES,
	STATUS_CODES,
} from '@atlassian/conversation-assistant-service-api/constants';
import { useMutation } from '@atlassian/conversation-assistant-service/common/utils/use-mutation';
import {
	type AssistanceServiceInjectedParams,
	type AssistanceServiceInputParam,
	createHookWithAssistanceService,
} from '@atlassian/conversation-assistant-service/services/assistance-service/main';

import { type UploadedFile } from '../attachtment-store/UploadedFile';
import { useFileAttachmentsStore } from '../attachtment-store/useFileAttachmentsStore';
import { isKnownUploadFileUserError } from './isKnownUploadFileUserError';
import { mapValidateErrorCodeToMessage } from './mapValidateErrorCodeToMessage';
import messages from './messages';

const renderFileInput = (
	ref: React.RefObject<HTMLInputElement>,
	onChange: React.ChangeEventHandler<HTMLInputElement>,
) => (
	<Box
		testId="file-upload-input"
		as="input"
		ref={ref}
		hidden
		type="file"
		multiple
		onChange={onChange}
	/>
);

export const useConversationFiles: (
	props: Omit<
		AssistanceServiceInjectedParams & {
			key: string;
			stableFileInputChange?: boolean;
		},
		'assistanceServiceInjectedParams'
	> &
		AssistanceServiceInputParam,
) => {
	uploadFiles: {
		isSubmitting: boolean;
	};
	anyFilesUploading: boolean;
	handleFilesSelected: (uploadedFiles: File[] | null) => Promise<void>;
	files: UploadedFile[];
	removeFile: (key: string, fileName: string) => void;
	clearFiles: (key: string) => void;
	clearAttachments: (key: string) => void;
	getFileInput: (ref: React.RefObject<HTMLInputElement>) => React.JSX.Element;
} = createHookWithAssistanceService(
	({
		key,
		stableFileInputChange = false,
		assistanceServiceInjectedParams: { assistanceService, experienceTracker },
	}: AssistanceServiceInjectedParams & { key: string; stableFileInputChange?: boolean }) => {
		di(useFlags, useAnalytics);
		const [files, { addFiles, removeFile, clearFiles, updateFileWithName, clearAttachments }] =
			useFileAttachmentsStore(key);
		const { formatMessage } = useIntl();
		const { sendAnalyticsEvent } = useAnalytics();
		const anyFilesUploading = useMemo(() => {
			return files.some((file) => file.isLoading);
		}, [files]);
		const { showFlag } = useFlags();

		const { mutate: mutateUploadFiles, isSubmitting: isSubmittingUploadFiles } = useMutation({
			mutationFn: (payload: { file: File }) => {
				experienceTracker.start({
					id: ExperienceName.UPLOAD_FILES_TO_CONVERSATION,
					name: ExperienceName.UPLOAD_FILES_TO_CONVERSATION,
				});

				addFiles(
					key,
					[
						{
							id: '',
							name: payload.file.name,
							isLoading: true,
							fileObject: payload.file,
						},
					],
					sendAnalyticsEvent,
				);

				return assistanceService.uploadFiles({
					files: [payload.file],
				});
			},
			onSuccess: ({ data }) => {
				data.forEach((file) => {
					updateFileWithName(key, file.name, {
						id: file.id,
						isLoading: false,
					});

					sendAnalyticsEvent({
						action: 'added',
						actionSubject: 'rovoChatContext',
						actionSubjectId: 'file',
						attributes: {
							fileType: file.type || 'unknown',
							fileSize: file.size || 0,
						},
					});
				});

				experienceTracker.succeed({
					name: ExperienceName.UPLOAD_FILES_TO_CONVERSATION,
				});
			},
			onError: (error, payload) => {
				const errorMessage = (() => {
					if (error.errorAttributes.statusCode === STATUS_CODES.UNSUPPORTED_FILE_TYPE) {
						return formatMessage(messages.unsupportedFile);
					}

					if (
						error.errorAttributes.errorCode === ASSISTANCE_SERVICE_ERROR_CODES.FILE_SIZE_TOO_LARGE
					) {
						return error.errorAttributes.rawErrorMessage;
					}

					// Not handled by /validation due to requirement of CSAM check, we need to store the file first.
					// Check: https://atlassian.slack.com/archives/C0677M19GU9/p1751941718503669?thread_ts=1751919192.446729&cid=C0677M19GU9
					if (error.errorAttributes.statusCode === STATUS_CODES.FILE_RAI_POLICY_VIOLATION) {
						return formatMessage(messages.fileRaiPolicyViolationError);
					}

					return formatMessage(messages.unknownError);
				})();

				updateFileWithName(key, payload.file.name, {
					isLoading: false,
					isPreviewLoading: false,
					error: errorMessage,
				});

				if (isKnownUploadFileUserError(error)) {
					experienceTracker.succeed({
						name: ExperienceName.UPLOAD_FILES_TO_CONVERSATION,
					});
					return;
				}

				experienceTracker.fail({
					name: ExperienceName.UPLOAD_FILES_TO_CONVERSATION,
					error,
				});
			},
		});

		const { mutate: mutateValidateFiles, isSubmitting: isSubmittingValidateFiles } = useMutation({
			mutationFn: (payload: { files: File[] }) => {
				experienceTracker.start({
					id: ExperienceName.VALIDATE_FILES,
					name: ExperienceName.VALIDATE_FILES,
				});

				return assistanceService.validateFiles({
					fileMetadata: payload.files.map((file) => ({
						originalFilename: file.name,
						contentType: file.type,
						size: file.size,
					})),
				});
			},
			onSuccess: () => {
				experienceTracker.succeed({
					name: ExperienceName.VALIDATE_FILES,
				});
			},
			onError: (error) => {
				experienceTracker.fail({
					name: ExperienceName.VALIDATE_FILES,
					error,
				});
			},
		});

		const handleFilesSelected = useCallback(
			async (uploadedFiles: File[] | null) => {
				if (!uploadedFiles?.length) {
					return;
				}

				const newFiles = uploadedFiles.filter((file) => !files.some((f) => f.name === file.name));

				// Validate files before uploading them
				const validateResponse = await mutateValidateFiles({
					files: newFiles,
				});

				const getErrorIcon = () => (
					<StatusErrorIcon
						label={formatMessage(messages.fileInvalidIconLabel)}
						color={token('color.icon.danger')}
					/>
				);

				if (!validateResponse) {
					showFlag({
						id: `conversation-files-fail-to-validate-files-${Date.now()}`,
						title: formatMessage(messages.failToValidateFilesTitle),
						description: formatMessage(messages.failToValidateFilesDescription),
						icon: getErrorIcon(),
					});
					return;
				}

				const { validFiles, invalidFiles } = newFiles.reduce<{
					validFiles: File[];
					invalidFiles: { fileName: string; errorCode: string }[];
				}>(
					(acc, file) => {
						const validationResult = validateResponse.validationResults.find(
							(result) => result.fileMetadata.originalFilename === file.name,
						);

						if (!validationResult) {
							acc.invalidFiles.push({
								fileName: file.name,
								errorCode: 'VALIDATION_RESULT_NOT_FOUND',
							});

							return acc;
						}

						if (validationResult.isSupported) {
							acc.validFiles.push(file);
						} else {
							acc.invalidFiles.push({
								fileName: validationResult.fileMetadata.originalFilename,
								errorCode: validationResult.errorCode ?? 'VALIDATION_RESULT_MISSING_ERROR_CODE',
							});
						}

						return acc;
					},
					{ validFiles: [], invalidFiles: [] },
				);

				if (invalidFiles.length) {
					showFlag({
						id: `conversation-files-validation-error-${Date.now()}`,
						title: formatMessage(messages.invalidFilesTitle, {
							count: invalidFiles.length,
						}),
						description: formatMessage(messages.invalidFilesDescription, {
							count: invalidFiles.length,
							fileNames: invalidFiles.map((file) => file.fileName).join(', '),
							reasons: Array.from(
								new Set(
									invalidFiles.map((file) =>
										formatMessage(mapValidateErrorCodeToMessage(file.errorCode)),
									),
								),
							).join(', '),
						}),
						icon: getErrorIcon(),
					});
				}

				if (!validFiles.length) {
					return;
				}

				validFiles.forEach((file) => {
					mutateUploadFiles({
						file,
					});
				});
			},
			[mutateUploadFiles, files, formatMessage, showFlag, mutateValidateFiles],
		);

		const latestHandleFilesSelectedRef = useRef(handleFilesSelected);
		latestHandleFilesSelectedRef.current = handleFilesSelected;

		const stableFileInputOnChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
			latestHandleFilesSelectedRef.current(Array.from(e.currentTarget.files ?? []));
			e.currentTarget.value = '';
		}, []);

		const getStableFileInput = useCallback(
			(ref: React.RefObject<HTMLInputElement>) => renderFileInput(ref, stableFileInputOnChange),
			[stableFileInputOnChange],
		);

		const getControlFileInput = useCallback(
			(ref: React.RefObject<HTMLInputElement>) =>
				renderFileInput(ref, (e) => {
					handleFilesSelected(Array.from(e.target.files ?? []));

					// Temporary until we only have a single file input and no ref param
					if (ref.current) {
						ref.current.value = '';
					}
				}),
			[handleFilesSelected],
		);
		const getFileInput = stableFileInputChange ? getStableFileInput : getControlFileInput;

		return {
			uploadFiles: {
				isSubmitting: isSubmittingUploadFiles || isSubmittingValidateFiles,
			},
			anyFilesUploading,
			handleFilesSelected,
			files,
			removeFile,
			clearFiles,
			clearAttachments,
			getFileInput,
		};
	},
);
